import { styled } from "@mui/material/styles";
import {
  Typography,
  TableContainer,
  Button,
  TableCell,
  Snackbar,
  Alert,
  Switch,
  Card,
  Chip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import colors from "assets/theme/base/colors";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useMaterialUIController } from "context";
import ListItemText from "@mui/material/ListItemText";
import { Tabs, Tab, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { Box, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

export const StyledTableContainer = styled(TableContainer)(() => ({
  display: "flex",
  backgroundColor: "transparent",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  flex: 1,
  boxShadow: "none",
  overflowY: "auto",
  marginTop: "20px",
}));

const ResponsiveTabs = ({
  tabs,
  activeTab,
  onChange,
  labelColor = "primary",
  bgColor = "#E59400",
  width = "600px", // Default width is 500px, but you can override this
}) => {
  const theme = useTheme();

  return (
    <Tabs
      value={activeTab}
      onChange={onChange}
      indicatorColor="transparent"
      textColor={labelColor}
      variant="scrollable"
      scrollButtons="none"
      sx={{
        backgroundColor: bgColor,
        padding: 0,
        height: "45px",
        width: "100%",
        maxWidth: width, // Dynamic width here
        boxShadow: "none",
        overflowX: "auto",
        "& .MuiTabs-flexContainer": {
          justifyContent: { xs: "start", sm: "center" },
        },
      }}
    >
      {tabs.map((tab, index) => (
        <Tab
          key={index}
          label={
            <span style={{ color: activeTab === index ? "#ffffff" : "rgb(24, 46, 84)" }}>
              {tab.label}
            </span>
          }
          sx={{
            backgroundColor: activeTab === index ? "#E59400" : "none",
            fontSize: { xs: "12px", sm: "14px", md: "16px" },
            minWidth: { xs: "80px", sm: "100px", md: "120px" },
            padding: { xs: "6px 8px", sm: "8px 12px" },
            color: activeTab === index ? "#ffffff" : "inherit",
            borderRadius: "8px",
            transition: "all 0.5s ease",
          }}
        />
      ))}
    </Tabs>
  );
};

ResponsiveTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeTab: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  labelColor: PropTypes.string,
  bgColor: PropTypes.string,
  width: PropTypes.string,
};

export default ResponsiveTabs;
export const getBackgroundColor = ({ transparentSidenav, whiteSidenav, darkMode }) => {
  if (transparentSidenav) return "#333333";
  if (whiteSidenav) return "#333333";
  if (darkMode) return "#333333";
  return "#333333";
};
export const CustomSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-thumb": {
    backgroundColor: "red",
    border: "2px solid white",
  },
  "& .Mui-checked .MuiSwitch-thumb": {
    backgroundColor: "#E59400",
    border: "2px solid white",
  },
  "& .MuiSwitch-track": {
    backgroundColor: "transparent",
    opacity: 1,
  },
  "& .Mui-checked + .MuiSwitch-track": {
    backgroundColor: "pink",
    opacity: 1,
  },
}));

const getDynamicTheme = ({ transparentSidenav, whiteSidenav, darkMode }) => {
  const bgColor = getBackgroundColor({ transparentSidenav, whiteSidenav, darkMode });

  return {
    "--ag-font-family": "Roboto, sans-serif", // Global grid font
    "--ag-font-size": "14px", // Default text size
    "--ag-foreground-color": "#fffffff",

    "--ag-header-background-color": "#333333",
    "--ag-header-foreground-color": "#ffffff",
    "--ag-header-height": "60px",
    "--ag-header-font-size": "12px", // Ensure size is applied here
    "--ag-header-font-weight": "normal", // Ensure weight is applied here

    "--ag-cell-horizontal-padding": "8px",
    "--ag-cell-font-size": "10px", // Ensure cell size is consistent

    "--ag-cell-height": "50px",
    "--ag-cell-text-color": "#ffffff",

    "--ag-row-height": "40px",
    "--ag-icon-size": "16px",
    "--ag-floating-filter-height": "30px", // Height of floating filter row
    "--ag-floating-filter-input-background": "#ffffff", // Light mode: white input
    "--ag-floating-filter-input-border": "1px solid #cccccc", // Light mode: gray border
    "--ag-floating-filter-input-text-color": "#333333", // Light mode: dark text
    "--ag-floating-filter-input-padding": "4px 8px",
  };
};

export const StyledAgGridContainer = styled(TableContainer)(
  ({ theme, transparentSidenav, whiteSidenav, darkMode }) => {
    const [controller] = useMaterialUIController();
    const dynamicTheme = getDynamicTheme(controller);
    const bgColor = getBackgroundColor({ transparentSidenav, whiteSidenav, darkMode });

    return {
      ...dynamicTheme,
      height: "100%",
      minHeight: "200px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "15px",
      backgroundColor: "transparent",
      boxShadow: "None",
      flexGrow: 1,
      overflow: "auto",
      "& .ag-root-wrapper": {
        flexGrow: 1,
        borderRadius: "12px",
        overflow: "hidden",
      },
      // Floating filter styling
      "& .ag-floating-filter-body": {
        height: "var(--ag-floating-filter-height)",
        display: "flex",
        alignItems: "center",
        padding: "0 4px",
      },
      "& .ag-floating-filter-input": {
        backgroundColor: "var(--ag-floating-filter-input-background)",
        border: "var(--ag-floating-filter-input-border)",
        color: "var(--ag-floating-filter-input-text-color)",
        borderRadius: "4px",
        fontSize: "12px",
        height: "24px", // Slightly smaller than container for alignment
        width: "100%",
        boxSizing: "border-box",
        "&:focus": {
          outline: "none",
          borderColor: darkMode ? "#888888" : "#007bff", // Focus state
        },
      },
      "& .ag-floating-filter-button": {
        backgroundColor: "var(--ag-floating-filter-button-background)",
        borderRadius: "3px",
        // padding: "2px",
        "&:hover": {
          backgroundColor: "var(--ag-floating-filter-button-hover-background)",
        },
      },
      "& .ag-header-row-floating-filter": {
        height: "var(--ag-floating-filter-height)", // Ensure row height matches
      },

      // Modern scrollbar styling
      "& ::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "& ::-webkit-scrollbar-thumb": {
        backgroundColor: "#bdbdbd", // Soft gray for a modern feel
        borderRadius: "6px",
      },
      "& ::-webkit-scrollbar-track": {
        backgroundColor: "#f5f5f5",
      },
      "& ::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#9e9e9e",
      },

      // Styling for AG Grid pagination
      "& .ag-paging-panel": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        fontSize: "12px",
        padding: "12px",
        backgroundColor: "#ffffff", // Keep pagination panel visible
        borderTop: `1px solid ${theme.palette.divider}`,
      },

      // Responsive adjustments
      [theme.breakpoints.down("lg")]: {
        "& .ag-paging-panel": {
          fontSize: "11px",
        },
      },
      [theme.breakpoints.down("md")]: {
        minHeight: "500px",
      },
      [theme.breakpoints.down("sm")]: {
        height: "60%",
        minHeight: "400px",
        "& .ag-paging-panel .ag-paging-page-size": {
          display: "none",
        },
      },
    };
  }
);

export const StyledAgGridContainerMis = styled(TableContainer)(
  ({ theme, transparentSidenav, whiteSidenav, darkMode }) => {
    const [controller] = useMaterialUIController();
    const dynamicTheme = getDynamicTheme(controller);
    const bgColor = getBackgroundColor({ transparentSidenav, whiteSidenav, darkMode });

    return {
      ...dynamicTheme,
      flexGrow: 1, // Occupy full space inside Paper
      height: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: bgColor, // Dynamic background
      borderRadius: "12px", // Modern rounded corners
      boxShadow: "none", // No shadow for a clean look

      "& .ag-root-wrapper": {
        flexGrow: 1,
        borderRadius: "12px",
        overflow: "hidden",
      },

      // Modern scrollbar styling
      "& ::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "& ::-webkit-scrollbar-thumb": {
        backgroundColor: "#bdbdbd", // Soft gray for a modern feel
        borderRadius: "6px",
      },
      "& ::-webkit-scrollbar-track": {
        backgroundColor: "#f5f5f5",
      },
      "& ::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#9e9e9e",
      },

      // Styling for AG Grid pagination
      "& .ag-paging-panel": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        fontSize: "12px",
        padding: "12px",
        backgroundColor: "#ffffff", // Keep pagination panel visible
        borderTop: `1px solid ${theme.palette.divider}`,
      },

      // Responsive adjustments
      [theme.breakpoints.down("lg")]: {
        "& .ag-paging-panel": {
          fontSize: "11px",
        },
      },
      [theme.breakpoints.down("md")]: {
        minHeight: "500px",
      },
      [theme.breakpoints.down("sm")]: {
        height: "60%",
        minHeight: "400px",
        "& .ag-paging-panel .ag-paging-page-size": {
          display: "none",
        },
      },
    };
  }
);

export const StyledAgGridContainerForDashBoard = styled(TableContainer)(
  ({ theme, transparentSidenav, whiteSidenav, darkMode }) => {
    const [controller] = useMaterialUIController();
    const dynamicTheme = getDynamicTheme(controller);
    const bgColor = getBackgroundColor({ transparentSidenav, whiteSidenav, darkMode });
    return {
      ...dynamicTheme,
      height: "auto",
      minHeight: "200px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "0px",
      flexGrow: 1,
      overflow: "auto",
      borderRadius: "10px",

      // Apply rounded borders and ensure content stays inside
      "& .ag-root-wrapper": {
        borderRadius: "10px",
        overflow: "hidden",
      },

      // Custom scrollbar styles
      "& ::-webkit-scrollbar": {
        width: "0px",
        height: "0px",
      },
      "& ::-webkit-scrollbar-thumb": {
        backgroundColor: bgColor,
        borderRadius: "2px",
        transition: "background-color 0.3s ease, border-radius 0.3s ease",
      },
      "& ::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#555",
      },
      "& ::-webkit-scrollbar-track": {
        backgroundColor: "#f1f1f1",
        transition: "background-color 0.3s ease",
      },
      "& .ag-paging-panel": {
        display: "flex",
        justifyContent: "center", // Centers horizontally
        alignItems: "center", // Centers vertically
        width: "100%",
        fontSize: "12px", // Default font size
        padding: "10px", // Default padding
        margin: "0 auto",
      },
      // Hover effect to increase scrollbar width
      "&:hover": {
        "& ::-webkit-scrollbar": {
          width: "6px", // Increase width on hover
          height: "6px", // Increase height on hover
          transition: "width 0.3s ease, height 0.3s ease",
        },
        "& ::-webkit-scrollbar-thumb": {
          backgroundColor: bgColor,
          borderRadius: "4px", // Adjust radius for better appearance
          transition: "background-color 0.3s ease, border-radius 0.3s ease",
        },
        "& ::-webkit-scrollbar-track": {
          backgroundColor: "#e0e0e0", // Slightly darker track color
          transition: "background-color 0.3s ease",
        },
      },
      scrollbarWidth: "thin",
      [theme.breakpoints.down(1700)]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height
        "& .ag-paging-panel": {
          fontSize: "10px", // Adjust font size
          padding: "10px", // Add padding
        },
      },
      [theme.breakpoints.down(1500)]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height
        "& .ag-paging-panel": {
          fontSize: "10px", // Adjust font size
          padding: "10px", // Add padding
        },
      },
      // Media queries for height adjustments
      [theme.breakpoints.down("xl")]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height
        "& .ag-paging-panel": {
          fontSize: "10px", // Adjust font size
          padding: "10px", // Add padding
        },
      },
      [theme.breakpoints.down("lg")]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height
        "& .ag-paging-panel": {
          fontSize: "9px", // Adjust font size
          padding: "10px", // Add padding
        },
      },
      [theme.breakpoints.down("md")]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height
        "& .ag-paging-panel": {
          fontSize: "10px", // Adjust font size
          padding: "10px", // Add padding
        },
        marginBottom: "10px",
      },
      [theme.breakpoints.down("sm")]: {
        height: "90vh", // Further reduce height for small screens
        minHeight: "300px", // Maintain minimum height,
        "& .ag-paging-panel": {
          fontSize: "10px", // Adjust font size
          padding: "10px", // Adjust padding
        },
        "& .ag-paging-panel .ag-paging-page-size": {
          display: "none", // Hide page size dropdown
        },
        "& .ag-paging-panel .ag-paging-row-summary-panel": {
          display: "none", // Hide row summary panel if present
        },
      },
    };
  }
);

export const StyledTypography = styled(Typography)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: bColor,
    fontSize: "1rem",
    width: "100%",
    marginTop: "10px",
    textAlign: "center",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 0px rgba(0, 0, 0, 0.4)",
    paddingBottom: "10px",
    paddingTop: "3px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "8px",
    },
  };
});
export const StyledTypographyA = styled(Typography)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: bColor,
    fontSize: "1rem",
    width: "100%",
    textAlign: "center",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 0px rgba(0, 0, 0, 0.4)",
    marginBottom: "5px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "0px",
    },
  };
});
export const StyledTypographyDailog = styled(Typography)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: bColor,
    fontSize: "0.9rem",
    textAlign: "center",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "0px",
    },
  };
});
export const StyledListItemText = styled(ListItemText)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: bColor,
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    marginRight: "20px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "0px",
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.9rem",
      fontWeight: "bold", // Styling for primary text (BankName)
      color: bColor, // Dynamic color for primary text
    },
    "& .MuiListItemText-secondary": {
      fontWeight: "bold",
      fontSize: "0.87rem", // Adjust font size for secondary text (AccountNumber)
      color: bColor, // Dynamic color for secondary text
    },
  };
});
export const StyledTypographyAA = styled(Typography)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: bColor,
    fontSize: "1rem",
    width: "90%",
    textAlign: "center",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      padding: "0px",
    },
  };
});
export const StyledDialogBoxTitle = styled(Typography)(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontWeight: "bold",
    color: "white", // Text color set to white
    fontSize: "0.9rem",
    width: "fit-content", // Adjust width dynamically based on content
    maxWidth: "500px", // Limit max width for better control
    textAlign: "center",
    whiteSpace: "nowrap", // Prevent text wrapping
    overflow: "hidden", // Hide overflow text
    textOverflow: "ellipsis", // Add ellipsis if text overflows
    backgroundColor: bColor, // Background color set to bColor
    borderRadius: "8px", // Added border radius
    padding: "10px 15px", // Additional padding
    [theme.breakpoints.down("md")]: {
      fontSize: "0.9rem",
      maxWidth: "300px", // Adjust max width for smaller screens
      padding: "5px 10px", // Reduced padding for smaller screens
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
      maxWidth: "200px", // Further reduce max width for extra small screens
      padding: "5px 10px", // Reduced padding for smaller screens
    },
  };
});

export const StyledButton = styled("button")(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    height: "40px",
    minWidth: "80px",
    maxWidth: "auto",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: "#E59400",
    color: "#FFFFFF",
    fontWeight: "bold",
    borderRadius: "10px",
    boxShadow: "1px 4px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease", // Ensure smooth transitions
    border: "none",
    cursor: "pointer", // Add pointer cursor for better UX
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: bColor,
      border: `1px solid #E59400`,
    },
    "&:active": {
      transform: "scale(0.95)", // Slightly reduce scale on click
      color: "#FFFFFF",
    },
    "&:focus": {
      outline: "none",
    },
  };
});
export const StyledCancelButton = styled("button")(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    height: "40px",
    minWidth: "80px",
    maxWidth: "auto",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: "#FFFFFF",
    color: "red",
    fontWeight: "bold",
    borderRadius: "10px",
    boxShadow: "1px 4px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    border: `1px solid red`,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgb(229, 225, 225)",
    },
    "&:active": {
      transform: "scale(0.95)",
      color: "#FFFFFF",
    },
    "&:focus": {
      outline: "none",
    },
  };
});
export const StyledButtonRec = styled("button")(({ theme, isSelected }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);
  const selectedBgColor = "#4CAF50"; // Green accent for selection
  const selectedTextColor = "#FFFFFF"; // White text for selected buttons

  return {
    position: "relative", // Enable pseudo-elements like the arrow
    height: "36px",
    minWidth: "70px",
    display: "block",
    maxWidth: "200px",
    padding: "8px 12px",
    backgroundColor: isSelected ? selectedBgColor : "#E59400",
    color: isSelected ? selectedTextColor : "#FFFFFF",
    fontWeight: "bold",
    fontSize: "14px",
    borderRadius: "8px",
    boxShadow: "1px 4px 4px rgba(0, 0, 0, 0.1)",
    border: isSelected ? `2px solid ${selectedBgColor}` : "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap", // Prevent text wrapping
    // overflow: "hidden", // Ensure no overflow issues
    // textOverflow: "ellipsis", // Show ellipsis if the text exceeds button width
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: isSelected ? selectedBgColor : "#FFFFFF",
      color: isSelected ? selectedTextColor : bColor,
      border: `1px solid ${bColor}`,
      transform: "scale(0.95)", // Scale effect
      transformOrigin: "center", // Prevent shifting during scale
      willChange: "transform", // Optimize performance for transform
    },
    "&:active": {
      transform: "scale(0.92)", // Slightly more scale on click
      transformOrigin: "center",
    },
    "&:focus": {
      outline: "none",
    },
    // Arrow indicator for the selected button
    "&::after": {
      content: isSelected ? '""' : "none",
      position: "absolute",
      bottom: "-10px", // Position below the button
      left: "50%",
      transform: "translateX(-50%)",
      width: "0",
      height: "0",
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderTop: `10px solid ${selectedBgColor}`, // Arrow color matches selected button
    },
    // Responsive styles
    [theme.breakpoints.down("md")]: {
      height: "32px",
      minWidth: "60px",
      padding: "6px 10px",
      fontSize: "12px",
      borderRadius: "6px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "28px",
      minWidth: "50px",
      padding: "4px 8px",
      fontSize: "10px",
      borderRadius: "4px",
    },
  };
});

export const StyledButtonLc = styled("button")(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    height: "40px",
    width: "40px",
    borderRadius: "50%", // Ensures the button is circular
    backgroundColor: "#E59400",
    marginLeft: "5px",
    color: "#FFFFFF",
    fontWeight: "bold",
    boxShadow: "1px 10px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease", // Smooth transitions
    border: "none",
    cursor: "pointer", // Pointer cursor for better UX
    display: "flex", // Ensures the button content is centered
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: bColor,
      border: `1px solid #E59400`,
    },
    "&:active": {
      transform: "scale(0.95)", // Slightly reduce scale on click
      color: "#FFFFFF",
    },
    "&:focus": {
      outline: "none",
    },
  };
});

export const StyledButtonL = styled("button")(({ theme }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    height: "30px",
    width: "90px",
    backgroundColor: "#E59400",
    color: "#FFFFFF",
    fontWeight: "bold",
    borderRadius: "8px",
    boxShadow: "1px 10px 4px rgba(0, 0, 0, 0.09)",
    transition: "all 0.3s ease", // Ensure smooth transitions
    border: "none",
    cursor: "pointer", // Add pointer cursor for better UX
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: bColor,
      border: `1px solid #E59400`,
    },
    "&:active": {
      transform: "scale(0.95)", // Slightly reduce scale on click
      color: "#FFFFFF",
    },
    "&:focus": {
      outline: "none",
    },
  };
});

export const StyledButtonC = styled("button")`
  ${({ theme }) => {
    const [controller] = useMaterialUIController();
    const bColor = getBackgroundColor(controller);

    return `
      background-color: #E59400;
      color: #ffffff;
      cursor: pointer;
      border: none;
      padding: 5px 10px;
      border-radius: 5px;
      margin-right: 3px;
      transition: transform 0.3s ease, background-color 0.3s ease;
      position: absolute;
      will-change: transform;
      top: 10px;
      right: 10px;
      z-index: 10;
      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95); // Slightly reduce scale on click
      }

      &:focus {
        outline: none; // Remove focus outline
      }
    `;
  }}
`;
export const StyledButtonAbort = styled("button")`
  ${({ theme }) => {
    const [controller] = useMaterialUIController();
    const bColor = getBackgroundColor(controller);

    return `
      background-color: #E07572;
      color: #ffffff;
      cursor: pointer;
      font-weight: 550;
      border: none;
      padding: 5px 10px;
      border-radius: 5px;
      margin-right: 3px;
      transition: transform 0.3s ease, background-color 0.3s ease;
      position: absolute;
      will-change: transform;
      top: 10px;
      right: 55px;
      z-index: 10;
      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95); // Slightly reduce scale on click
      }

      &:focus {
        outline: none; // Remove focus outline
      }
    `;
  }}
`;
export const StyledButtonCF = styled("button")`
  ${({ theme }) => {
    const [controller] = useMaterialUIController();
    const bColor = getBackgroundColor(controller);

    return `
      background-color: #E59400;
      color: #ffffff;
      cursor: pointer;
      border: none;
      padding: 5px 10px;
      border-radius: 5px;
      margin-right: 3px;
      transition: transform 0.3s ease, background-color 0.3s ease;
      position: absolute;
      will-change: transform;
      top: 20px;
      right: 10px;
      z-index: 10;
      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95); // Slightly reduce scale on click
      }

      &:focus {
        outline: none; // Remove focus outline
      }
    `;
  }}
`;
export const StyledButtonAnchor = styled("button")`
  ${({ theme }) => {
    const [controller] = useMaterialUIController();
    const bColor = getBackgroundColor(controller);

    return `
      background-color: #E59400;
      color: #ffffff;
      cursor: pointer;
      font-weight: 550;
      border: none;
      padding: 5px 10px;
      border-radius: 5px;
      margin-right: 3px;
      transition: transform 0.3s ease, background-color 0.3s ease;
      will-change: transform;
      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95); // Slightly reduce scale on click
      }

      &:focus {
        outline: none; // Remove focus outline
      }
    `;
  }}
`;
// View and Delete Buttons with specific styles
export const ViewButton = styled("button")(() => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return `
    background-color: #E59400;
    color: #ffffff;
    cursor: pointer;
    border: none;
    padding: 5px 10px;
    border-radius: 5px;
    transform: scale(1); /* Default scale */
    transition: transform 0.3s ease, background-color 0.3s ease;
    will-change: transform;
    &:hover {
      transform: scale(1.1); /* Scale up on hover */
    }
    &:active {
      transform: scale(0.95); /* Slightly reduce scale on click */
    }
    &:focus {
      outline: none; // Remove focus outline
    }
  `;
});

export const DeleteButton = styled("button")(() => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    backgroundColor: "#ffffff",
    color: "red",
    border: `1px solid red`,
    padding: "3px 10px",
    borderRadius: "5px",
    marginLeft: "5px",
    transition: "transform 0.3s ease",
    willChange: "transform",
    "&:hover": {
      transform: "scale(1.05)",
    },
  };
});

export const ButtonBox = styled("div")({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  height: "70px",
  // width: "45%", // To give some space between the boxes
  padding: "10px",
  gap: "10px",
  borderRadius: "10px",
  // border: "1px solid #ccc", // Light border around the boxes
  // boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
});

// TableCell styles with responsive font size
export const StyledTableCell = styled(TableCell)(({ theme, boxShadow }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    fontSize: boxShadow ? "16px" : "13px", // Default font size
    fontWeight: "normal",
    color: bColor,
  };
});

export const CustomTableCell = styled(TableCell)(
  ({ theme, minWidth, maxWidth, flexGrow, fontWeight }) => {
    const [controller] = useMaterialUIController();
    const bColor = getBackgroundColor(controller);

    return {
      fontSize: theme.breakpoints.down("xs") ? "14px" : "0.95rem",
      fontWeight: fontWeight || "500", // Default fontWeight if none is provided
      minWidth: minWidth || "auto", // Use provided minWidth or auto as default
      flexGrow: flexGrow || 0, // Use provided flexGrow or 0 as default
      color: bColor, // Dynamic color from controller
    };
  }
);

// Styled Alert component
export const StyledAlert = styled(Alert)(() => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    backgroundColor: bColor,
    color: "#ffffff",
    "& .MuiAlert-icon": {
      color: "#ffffff",
      fontSize: "1.5rem",
    },
  };
});

// Styled Snackbar component
export const StyledSnackbar = styled(Snackbar)(({ theme, severity }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    marginTop: theme.spacing(5),
    "& .MuiPaper-root": {
      backgroundColor: severity === "error" ? theme.palette.error.dark : bColor,
      color: "#ffffff",
      fontWeight: "bold",
      borderRadius: "10px",
    },
  };
});
// IconButton with custom color for icons
export const StyledIconButton = styled(IconButton)(({ color }) => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    padding: "10px",
    marginLeft: "0px",
    marginRight: "5px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    svg: {
      color: color || bColor,
    },
  };
});
export const ModernHeader = ({ label, tooltip }) => {
  return (
    <Tooltip title={tooltip || label} arrow>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          px: 2,
          py: 1.8,
          background: "linear-gradient(135deg, #FFE0B2,rgb(199, 206, 3))",
          borderRadius: "8px",
          gap: 1,
          mb: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          width: "fit-content",
          borderLeft: "4px solid rgba(3, 9, 15, 0.73)", // left accent stripe
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#2c3e50",
            textTransform: "capitalize",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

ModernHeader.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};
export const inputStyle = () => {
  const [controller] = useMaterialUIController();
  const bColor = getBackgroundColor(controller);

  return {
    a: bColor,
    b: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: "1px",
          borderColor: bColor, // Default border color
        },
        "&:hover fieldset": {
          borderColor: "green", // Border color on hover
        },
        "&.Mui-focused fieldset": {
          borderColor: "#20c997", // Border color when focused
        },
        "& input": {
          color: bColor, // Text color
        },
        "& .MuiInputLabel-root": {
          color: bColor, // Label color
          "&.Mui-focused": {
            color: "#20c997", // Label color when focused
          },
        },
        "&.Mui-disabled": {
          "& fieldset": {
            borderColor: bColor, // Border color when disabled
          },
          "& input": {
            color: "gray", // Text color when disabled
          },
          "& .MuiInputLabel-root": {
            color: "gray", // Label color when disabled
          },
        },
        "& input[readonly]": {
          color: "#808080", // Text color when read-only
          pointerEvents: "none", // Prevent user interaction
        },
        "&[readonly] fieldset": {
          borderColor: "#808080", // Border color for read-only
          borderStyle: "dashed", // Optional: Distinct style for read-only
        },
        "& input[readonly] + .MuiInputLabel-root": {
          color: "#808080", // Label color for read-only
        },
      },
    },
  };
};
export const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  bqqorderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
  },
}));

export const HeaderBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#e59500d1",
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
}));

export const GrossButton = styled(Button)(({ theme }) => ({
  backgroundColor: "white",
  color: "black",
  fontWeight: "bold",
  marginTop: "30px",
  padding: theme.spacing(1, 2),
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "white",
  },
}));

export const CumulativeChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: 40,
  zIndex: "10",
  backgroundColor: "#E59400",
  color: "white",
  fontSize: "0.75rem",
  fontWeight: "bold",
  border: "1px solid white",
}));
